import Header from '../components/Header';
import Footer from '../components/Footer';
import { FileText, AlertCircle, Scale } from 'lucide-react';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-regal-dark">
      <Header />
      <main className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <article className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                Terms & Conditions
              </h1>
              <p className="text-gray-400">Last updated: December 2, 2025</p>
            </div>
            
            <div className="prose prose-invert max-w-none">
              <div className="bg-regal-gray rounded-xl p-8 mb-8 border border-gray-800">
                <p className="text-xl text-gray-300 leading-relaxed">
                  These Terms and Conditions govern your use of the Regal Play website (regal-play.com). By accessing or using our website, you agree to be bound by these terms. Please read them carefully before using our services.
                </p>
              </div>

              <div className="space-y-8 text-gray-300 leading-relaxed">
                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <FileText className="w-8 h-8 text-regal-red" />
                    <h2 className="text-3xl font-bold text-white">Acceptance of Terms</h2>
                  </div>
                  <p>
                    By accessing and using the Regal Play website, you accept and agree to be bound by these Terms and Conditions and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this website.
                  </p>
                  <p>
                    We reserve the right to modify these terms at any time without prior notice. Your continued use of the website following any changes constitutes acceptance of those changes. It is your responsibility to review these terms periodically to ensure you understand current terms and conditions.
                  </p>
                  <p>
                    These terms apply to all visitors, users, and others who access or use our website. By using our services, you represent that you are at least 18 years old and have the legal capacity to enter into these terms.
                  </p>
                </section>

                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <AlertCircle className="w-8 h-8 text-regal-red" />
                    <h2 className="text-3xl font-bold text-white">Age Restrictions</h2>
                  </div>
                  <p>
                    Our website is intended solely for users who are 18 years of age or older. Online gambling is restricted to adults, and it is illegal for individuals under 18 to access gambling services in the United Kingdom.
                  </p>
                  <p>
                    By using our website, you confirm that you are at least 18 years old and that you meet the legal age requirements for gambling in your jurisdiction. We reserve the right to verify your age and may refuse service to anyone who cannot provide proof of age.
                  </p>
                  <p>
                    If we discover that a user is under 18, we will immediately terminate their access to our website and delete any personal information we may have collected. Parents and guardians should monitor their children's internet usage to prevent access to gambling-related websites.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Use of Website</h2>
                  <p>
                    You may use our website for lawful purposes only and in accordance with these Terms and Conditions. You agree not to use the website in any way that violates any applicable laws or regulations, or that could damage, disable, or impair the website or interfere with any other party's use of the website.
                  </p>
                  <p>
                    Prohibited activities include but are not limited to: attempting to gain unauthorized access to our systems, introducing viruses or malicious code, using automated systems to access the website without permission, or engaging in any activity that disrupts or interferes with website operations.
                  </p>
                  <p>
                    You agree not to reproduce, duplicate, copy, sell, resell, or exploit any portion of the website without our express written permission. All content on our website, including text, graphics, logos, and images, is the property of Regal Play or its content suppliers and is protected by copyright and other intellectual property laws.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Content and Information</h2>
                  <p>
                    We strive to provide accurate and up-to-date information about online casinos, bonuses, and gambling services. However, we make no warranties or representations about the accuracy, completeness, or reliability of any information provided on our website.
                  </p>
                  <p>
                    Casino bonuses, terms, and conditions change frequently. While we update our content regularly, information may become outdated. We recommend verifying all bonus terms, wagering requirements, and casino conditions directly with the casino operator before making deposits or claiming offers.
                  </p>
                  <p>
                    Our reviews, ratings, and recommendations are based on our analysis and research at the time of publication. Opinions expressed are our own and are provided for informational purposes only. They should not be considered as financial, legal, or professional advice.
                  </p>
                  <p>
                    We reserve the right to modify, update, or remove any content on our website at any time without prior notice. We are not obligated to update information that may become inaccurate or outdated.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Affiliate Relationships</h2>
                  <p>
                    Regal Play operates as an independent review and comparison website. Some links on our pages are affiliate links, meaning we may receive a commission if you click through or sign up through these links. This commission structure helps us maintain and operate our website.
                  </p>
                  <p>
                    Our affiliate relationships do not influence our reviews, ratings, or recommendations. We maintain complete editorial independence and provide honest, unbiased information regardless of whether we have affiliate partnerships with featured casinos. Our commitment is to serve users with accurate, helpful information.
                  </p>
                  <p>
                    When you click on affiliate links, you will be redirected to third-party casino websites. We are not responsible for the content, services, terms, or practices of these external sites. Any transactions or agreements you enter into with casino operators are between you and the operator, not with Regal Play.
                  </p>
                  <p>
                    We do not guarantee that you will receive any bonuses, promotions, or services advertised by third-party casinos. All casino offers are subject to the terms and conditions of the respective operators, and we are not responsible if casinos change or withdraw offers.
                  </p>
                </section>

                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <Scale className="w-8 h-8 text-regal-red" />
                    <h2 className="text-3xl font-bold text-white">Limitation of Liability</h2>
                  </div>
                  <p>
                    Regal Play provides information and comparison services on an "as is" and "as available" basis. We make no warranties, express or implied, regarding the website's operation, accuracy, reliability, or availability.
                  </p>
                  <p>
                    We are not liable for any losses, damages, or expenses arising from your use of our website, including but not limited to: losses incurred from gambling at casinos featured on our site, errors or inaccuracies in information provided, or inability to access the website.
                  </p>
                  <p>
                    We are not responsible for any disputes, issues, or problems you may experience with casino operators. Any complaints, disputes, or issues regarding casino services, bonuses, or withdrawals should be directed to the respective casino operator and relevant regulatory authorities.
                  </p>
                  <p>
                    To the maximum extent permitted by law, our total liability for any claims arising from your use of our website shall not exceed the amount you paid to us, if any, for accessing our services. In no event shall we be liable for indirect, incidental, special, or consequential damages.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Responsible Gambling</h2>
                  <p>
                    Regal Play is committed to promoting responsible gambling. We provide information about responsible gambling tools, support resources, and warning signs of problem gambling. However, we are not responsible for monitoring or controlling individual gambling behavior.
                  </p>
                  <p>
                    It is your responsibility to gamble responsibly and within your means. Never gamble with money you cannot afford to lose, and never gamble when under the influence of alcohol or drugs. Set limits on your gambling activities and seek help if gambling becomes problematic.
                  </p>
                  <p>
                    If you believe you may have a gambling problem, we encourage you to seek help from organizations such as BeGambleAware, GamCare, or your healthcare provider. We provide links to support resources, but we are not a substitute for professional help or treatment.
                  </p>
                  <p>
                    We support self-exclusion programs and responsible gambling measures implemented by casino operators. If you choose to self-exclude, you should do so directly with individual casino operators or through services like GamStop, which applies to all UK-licensed casinos.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Intellectual Property</h2>
                  <p>
                    All content on the Regal Play website, including text, graphics, logos, images, and software, is the property of Regal Play or its licensors and is protected by copyright, trademark, and other intellectual property laws.
                  </p>
                  <p>
                    You may not reproduce, distribute, modify, create derivative works, publicly display, or otherwise use our content without our express written permission. Unauthorized use of our content may violate copyright, trademark, and other laws and could result in legal action.
                  </p>
                  <p>
                    The "Regal Play" name and logo are trademarks of Regal Play. You may not use these trademarks without our prior written consent. Any unauthorized use of our trademarks is strictly prohibited.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Termination</h2>
                  <p>
                    We reserve the right to terminate or suspend your access to our website immediately, without prior notice, for any reason, including if you breach these Terms and Conditions or engage in any prohibited activities.
                  </p>
                  <p>
                    Upon termination, your right to use the website will cease immediately. We may delete your account and any information associated with it, subject to our Privacy Policy and legal obligations regarding data retention.
                  </p>
                  <p>
                    All provisions of these Terms and Conditions that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Governing Law</h2>
                  <p>
                    These Terms and Conditions are governed by and construed in accordance with the laws of England and Wales. Any disputes arising from these terms or your use of our website shall be subject to the exclusive jurisdiction of the courts of England and Wales.
                  </p>
                  <p>
                    If any provision of these Terms and Conditions is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect. The invalid or unenforceable provision shall be replaced with a valid provision that most closely reflects the intent of the original provision.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Contact Information</h2>
                  <p>
                    If you have questions about these Terms and Conditions, please contact us:
                  </p>
                  <div className="bg-regal-gray rounded-lg p-6 border border-gray-800 mt-4">
                    <p className="text-white font-semibold mb-2">Regal Play</p>
                    <p className="text-gray-300">Email: info@regal-play.com</p>
                    <p className="text-gray-300">Phone: +44-0544-403-303</p>
                    <p className="text-gray-300">Address: London, United Kingdom</p>
                  </div>
                </section>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsConditions;

